








































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientTestSettings, ClientTestSettingsValue, ClientUserRow } from "@/data/models/ClientAdministrationRow";
import { getClientUsersForAdministration, setClientTestSettings } from "@/data/repository/ClientAdministrationRepository";
import { getLastHundredApkVersions, getLastHundredDryingInfoConfigVersions } from "@/data/repository/AdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  TestUserChoice,
  testSettingsDiffer,
  testSettingsErrorKey,
  testUserChoices,
  toTestSettingsValue,
} from "@/utils/ClientTestSettings";

/** What `show()` resolves with: the saved settings, CHANGED_MEANWHILE (the caller reloads), or null. */
export type TestSettingsDialogResult = ClientTestSettings | "CHANGED_MEANWHILE" | null;

const EMPTY_SETTINGS: ClientTestSettings = { isTestSystem: false, appVersion: null, roomInfoConfigVersion: null, testUsers: [] };

/** A stored value missing from the recent choices stays offered, so the field still shows it. */
function withCurrent(choices: string[], current: string | null): string[] {
  return current !== null && !choices.includes(current) ? [current, ...choices] : choices;
}

/**
 * Edits a client's test settings (#2610070): the ERP-Simulator and the Test-App targets (test app,
 * test Beschreibung, test users), with the same choices Mandant Test-App offered in IRES Office: the
 * last hundred app files and Beschreibung versions, and the client's users who are not deleted. The
 * whole settings are sent with the values loaded, so IRES Cloud can refuse a change made meanwhile.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientTestSettingsDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientTestSettings = { ...EMPTY_SETTINGS };
  form: ClientTestSettingsValue = toTestSettingsValue(EMPTY_SETTINGS);

  isLoadingVersions = false;
  isLoadingUsers = false;
  usersLoadFailed = false;
  appVersions: string[] = [];
  roomInfoConfigVersions: string[] = [];
  users: ClientUserRow[] = [];
  /** The test users listed first: the selection when the list last opened (focus), not the live one. */
  selectedFirst: string[] = [];

  private resolve: ((result: TestSettingsDialogResult) => void) | null = null;

  get yesNoItems() {
    return [
      { text: this.$t("common.yes").toString(), value: true },
      { text: this.$t("common.no").toString(), value: false },
    ];
  }

  get appVersionItems(): string[] {
    return withCurrent(this.appVersions, this.stored.appVersion);
  }

  get roomInfoConfigVersionItems(): string[] {
    return withCurrent(this.roomInfoConfigVersions, this.stored.roomInfoConfigVersion);
  }

  get userItems(): TestUserChoice[] {
    return testUserChoices(
      this.users,
      this.stored.testUsers,
      this.$t("admin.clients.detail.testSettings.inactiveUser").toString(),
      this.$t("admin.clients.detail.testSettings.unknownUser").toString(),
      this.selectedFirst
    );
  }

  /** Taken when the field gains focus, which opening the list does; ticks while it is open keep their place. */
  sortSelectedFirst() {
    this.selectedFirst = [...this.form.userIds];
  }

  get hasChanged(): boolean {
    return testSettingsDiffer(toTestSettingsValue(this.stored), this.form);
  }

  /**
   * @param clientId - the client.
   * @param stored - its test settings as the detail loaded them.
   */
  show(clientId: string, stored: ClientTestSettings): Promise<TestSettingsDialogResult> {
    this.clientId = clientId;
    this.stored = { ...stored, testUsers: [...stored.testUsers] };
    this.form = toTestSettingsValue(stored);
    this.selectedFirst = [...this.form.userIds];
    this.baseDialog.show({
      title: this.$t("admin.clients.testSettings.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    this.loadChoices();
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** Not awaited by `show()`: the fields are usable at once and fill in as the choices arrive. */
  async loadChoices() {
    const clientId = this.clientId;
    this.isLoadingVersions = true;
    this.isLoadingUsers = true;
    this.usersLoadFailed = false;
    this.users = [];

    const versions = Promise.all([getLastHundredApkVersions(), getLastHundredDryingInfoConfigVersions()])
      .then(([appVersions, roomInfoConfigVersions]) => {
        this.appVersions = appVersions;
        this.roomInfoConfigVersions = roomInfoConfigVersions;
      })
      // Without the lists the stored values stay offered and can still be cleared.
      .catch(() => undefined)
      .finally(() => (this.isLoadingVersions = false));

    const users = getClientUsersForAdministration(clientId)
      .then((list) => {
        // A reopened dialog for another client must not take this answer.
        if (clientId === this.clientId) {
          this.users = list.users;
        }
      })
      .catch(() => {
        if (clientId === this.clientId) {
          this.usersLoadFailed = true;
        }
      })
      .finally(() => (this.isLoadingUsers = false));

    await Promise.all([versions, users]);
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged) {
      return;
    }
    this.busy = true;
    try {
      // A cleared autocomplete gives null; anything else is the chosen text.
      const value: ClientTestSettingsValue = {
        ...this.form,
        appVersion: this.form.appVersion || null,
        roomInfoConfigVersion: this.form.roomInfoConfigVersion || null,
      };
      const saved = await setClientTestSettings(this.clientId, toTestSettingsValue(this.stored), value);
      SnackbarModule.showSuccess({ key: "admin.clients.testSettings.saved" });
      this.close(saved ?? "CHANGED_MEANWHILE");
    } catch (error) {
      const key = testSettingsErrorKey(error);
      SnackbarModule.showError({ key });
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: TestSettingsDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: TestSettingsDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
